import { ErrorBoundary } from '@sentry/react';
import { Suspense, useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import App, { AppBridge } from './App';
import { ReactComponent as ErrorSvg } from './Assets/error.svg';
import { ReactComponent as Logo } from './Assets/yesbank/YesBank_WebView.svg';
import { ReactComponent as JFSErrorIcon } from './Assets/JFS/ErrorIcon.svg';
import './index.css';
import { pushEventToParent } from './Utils/eventUtils';
import {
  getAltJourney,
  setAltJourney,
  setAltJourneyTheme,
} from './Utils/storageUtils';
import { altJourneyType, altJourneyTheme } from './Utils/types';
import { DotLottiePlayer } from '@dotlottie/react-player';
import NativeLoader from './Components/NativeLoader';
import { getLottiePath } from './Utils/assetUtils';

export const renderErrorScreen = ({ error }: any) => {
  import('./Utils/sentryUtils').then(({ captureException }) => {
    captureException(error, { isErrorBoundary: true });
  });

  const isJFS = process.env.REACT_APP_TENANT_ID_JFS !== undefined;

  if (isJFS) {
    return (
      <div className='jfsErrorContainer'>
        <div className='jfsContentWrapper'>
          <div className='jfsErrorIcon'>
            <JFSErrorIcon />
          </div>

          <h1 className='jfsErrorTitle'>Something went wrong.</h1>

          <p className='jfsErrorDescription'>We ran into a technical issue.</p>

          {process.env.REACT_APP_IS_MOCK === 'true' && error && (
            <div className='jfsErrorDetails'>{error.toString()}</div>
          )}
        </div>

        <div className='jfsButtonContainer'>
          <button
            className='jfsGoBackButton'
            onClick={() => {
              if (AppBridge && AppBridge.closeWebview) {
                AppBridge.closeWebview();
              }
            }}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  } else {
    return (
      <div className='errorBoundaryRoot'>
        <ErrorSvg />
        <span className='errorBoundaryTitle'>Sorry!</span>
        <span className='errorBoundaryDescription'>
          Unfortunately, we have run into a technical issue
        </span>
        {process.env.REACT_APP_IS_MOCK === 'true' ? (
          <span>{error?.toString() ? error?.toString() : ''}</span>
        ) : (
          <></>
        )}
        <button
          className='errorButton'
          onClick={() => {
            pushEventToParent({ eventCode: 'GO_BACK' });
          }}
        >
          Go Back
        </button>
      </div>
    );
  }
};

const RootComponent = () => {
  const [isYesOnlineJourney, setIsYesOnlineJourney] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const meta = urlParams.get('meta');

    if (meta) {
      setAltJourney(altJourneyType.YESONLINE);
      setAltJourneyTheme(altJourneyTheme.YesGlory);
    }

    setIsYesOnlineJourney(getAltJourney() === altJourneyType.YESONLINE);
  }, []);

  return (
    <BrowserRouter basename={process.env.REACT_APP_BASE_PATH}>
      {isYesOnlineJourney && (
        <div className='yesonline-logo-container'>
          <Logo className='yesonline-logo' />
        </div>
      )}
      <Routes>
        <Route
          path={'*'}
          element={
            <Suspense
              fallback={
                process.env.REACT_APP_TENANT_ID_JFS !== undefined ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100vh',
                      background: '#fff',
                    }}
                  >
                    <DotLottiePlayer
                      src={getLottiePath('jfsLoader.lottie')}
                      autoplay
                      loop
                      style={{
                        height: 48,
                        width: 48,
                      }}
                    />
                  </div>
                ) : (
                  <NativeLoader />
                )
              }
            >
              <ErrorBoundary fallback={renderErrorScreen}>
                <App />
              </ErrorBoundary>
            </Suspense>
          }
        ></Route>
      </Routes>
    </BrowserRouter>
  );
};

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(<RootComponent />);
